import React, { useEffect, useState, Children } from 'react';
import { UploadIcon, X, CheckCircle, AlertCircle, FileText, Image as ImageIcon, Zap, Shield, Info, Lock, FileUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { validateFiles, uploadFiles, triggerAsyncAnalysis } from '../services/uploadService';
import { UploadProgress } from '../types/scan';
import { ForensicTerminalOverlay } from '../components/Upload/ForensicTerminalOverlay';
import { NotificationBanner } from '../components/Notifications/NotificationBanner';
import { ModeSelector, AnalysisMode } from '../components/Upload/ModeSelector';
import { useScans } from '../hooks/useScans';
import { useScanCredits } from '../hooks/useScanCredits';
import { usePlanEntitlement } from '../hooks/usePlanEntitlement';
import { useCheckout } from '../hooks/useCheckout';
import { PlanOptionCards } from '../components/Billing/PlanOptionCards';
import { paidPlans, type CheckoutPlanId } from '../data/pricingPlans';
import { supabase } from '../lib/supabaseClient';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackScanCompleted, trackScanFailed, trackScanStarted, type ScanInputType } from '../utils/analytics';
import { trackEvent, trackEventOnce, trackCtaClick } from '../utils/visitorTracking';
import { ScanResultModal } from '../components/Scan/ScanResultModal';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 15
    }
  }
};
export function UploadPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    scans
  } = useScans(user?.id);
  const {
    showToast
  } = useToast();
  const {
    isPro,
    scansCount,
    loading: loadingCredits,
    canScan,
    hasLoaded: creditsLoaded,
    refetchCredits,
    verifyCanScan,
    refetchError
  } = useScanCredits();
  // Credits are re-verified against the backend inside handleSubmit, so an
  // unread profile row must never hard-block the form — only a confirmed
  // zero-credit state does.
  const creditsUnknown = !loadingCredits && !creditsLoaded;
  const blockedByCredits = creditsLoaded && !canScan;
  // The user's real plan + entitlement, used for every status line and CTA.
  const {
    planId,
    planLabel,
    accessLabel,
    unlimited,
    loading: loadingPlan
  } = usePlanEntitlement();
  const {
    onCheckout,
    pendingPlan
  } = useCheckout('upload_page');
  // Auth guard: redirect unauthenticated users to login (after auth resolves)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth', {
        replace: true
      });
    }
  }, [authLoading, user, navigate]);
  const [conversationName, setConversationName] = useState('');
  const [conversationText, setConversationText] = useState(location.state?.initialText || '');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // PLAN GATE: Screenshots are a paid feature. They're allowed for
  //   - Pro / unlimited users (isPro === true)
  //   - Pay-per-scan users (anyone who has purchased a scan: either has
  //     credits beyond the bundled signup freebie, OR has already used
  //     their free scan and still has scans available)
  // They are BLOCKED for users still on the free-trial credit only.
  const screenshotsAllowed = isPro || scansCount > 1 || scansCount > 0 && scans.length > 0;
  // Every analysis mode is available to every user — no mode is locked.
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallReason, setPaywallReason] = useState<'credits' | 'mode'>('credits');
  const [showUpgradePanel, setShowUpgradePanel] = useState(false);
  const [selectedMode, setSelectedMode] = useState<AnalysisMode>(isPro || scansCount > 1 ? 'relationships' : 'default');
  useEffect(() => {
    if (!showPaywallModal) setPaywallReason('credits');
  }, [showPaywallModal]);
  const [uploading, setUploading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'pending' | 'processing' | 'completed' | 'failed'>('idle');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  // New state for scan results
  const [scanResult, setScanResult] = useState<{
    score?: number;
    riskLevel?: string;
    errorMessage?: string;
  }>({});
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  // CRITICAL: Kill switch - force exit processing state when analysisStatus changes
  // This ensures UI never stays stuck in "Processing" after a response
  useEffect(() => {
    if (analysisStatus === 'completed' || analysisStatus === 'failed') {
      console.log('[KILL SWITCH] Forcing uploading=false, status:', analysisStatus);
      setUploading(false);
    }
  }, [analysisStatus]);
  // Track page view on mount
  useEffect(() => {
    if (!loadingCredits && user?.id) {
      trackCriticalEvent('Upload_Page_Viewed', {
        is_pro: isPro,
        scans_remaining: scansCount,
        has_previous_scans: scans.length > 0
      });
    }
  }, [loadingCredits, user?.id, isPro, scansCount, scans.length]);
  useEffect(() => {
    const premiumModes: AnalysisMode[] = ['relationships', 'ex', 'friends', 'family', 'workplace'];
    if (!isPro && scansCount === 0 && premiumModes.includes(selectedMode)) {
      setSelectedMode('default');
    }
  }, [isPro, scansCount, selectedMode]);
  // Track mode selection
  useEffect(() => {
    if (selectedMode && !loadingCredits && user?.id) {
      const premiumModes: AnalysisMode[] = ['relationships', 'ex', 'friends', 'family', 'workplace'];
      trackCriticalEvent('Analysis_Mode_Selected', {
        mode: selectedMode,
        is_premium_mode: premiumModes.includes(selectedMode),
        user_plan: planId
      });
    }
  }, [selectedMode]);
  /**
   * Every paid CTA on this page routes through the one checkout abstraction.
   * No payment logic lives here — the plan identifier is handed to the
   * existing payment handler, which talks to the Edge Function.
   */
  const handleCheckout = (plan: CheckoutPlanId) => {
    if (!user?.email || !user?.id) {
      setError('You must be logged in to purchase');
      return;
    }
    void trackCriticalEvent('Payment_Initiated', {
      plan_type: plan,
      trigger_location: 'upload_page',
      current_plan: planId
    });
    trackCtaClick(plan, 'upload_page', 'paystack_checkout');
    void trackEvent('premium_checkout_started', {
      plan,
      location: 'upload_page'
    });
    setError(null);
    void onCheckout(plan);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Defensive: even if the UI is gated, refuse files from free-tier users.
    if (!screenshotsAllowed) {
      setError('Screenshot uploads are a paid feature. Upgrade or purchase a scan to unlock.');
      e.target.value = '';
      return;
    }
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validationErrors = validateFiles(files);
      if (validationErrors.length > 0) {
        setError(validationErrors[0].message);
        return;
      }
      setSelectedFiles(files);
      setError(null);
    }
  };
  // If the user's plan status changes to free-tier (e.g. after using a credit),
  // drop any pending screenshots so we never submit them on a downgrade.
  useEffect(() => {
    if (!screenshotsAllowed && selectedFiles.length > 0) {
      setSelectedFiles([]);
    }
  }, [screenshotsAllowed, selectedFiles.length]);
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setScanResult({});
    // CRITICAL: Disable button immediately to prevent double-tap
    // The DB has an atomic lock, but we also guard on the frontend
    if (uploading) return;
    setUploading(true);
    if (!user) {
      setError('You must be logged in to upload files');
      setUploading(false);
      return;
    }
    // CRITICAL: Verify with backend before allowing scan (not just local state)
    console.log('[SUBMIT] Verifying scan permission with backend...');
    const verification = await verifyCanScan();
    if (!verification.allowed) {
      console.log('[SUBMIT] Backend blocked scan:', verification.reason);
      if (verification.reason === 'NO_CREDITS') {
        // Track paywall display
        await trackCriticalEvent('Paywall_Displayed', {
          trigger_action: 'upload_attempt',
          is_pro: isPro,
          scans_remaining: 0,
          reason: 'backend_verification_no_credits'
        });
        setShowPaywallModal(true);
        setUploading(false);
        return;
      }
      if (verification.reason === 'VERIFICATION_FAILED') {
        setError('Unable to verify scan credits. Please try again.');
        showToast('Credit verification failed. Please refresh and try again.', 'error');
        setUploading(false);
        return;
      }
      if (verification.reason === 'NOT_LOGGED_IN') {
        setError('You must be logged in to upload files');
        setUploading(false);
        return;
      }
      // Generic fallback
      setError('Unable to start scan. Please try again.');
      setUploading(false);
      return;
    }
    console.log('[SUBMIT] Backend verified scan is allowed');
    if (!conversationName.trim()) {
      setError('Please enter a conversation name');
      setUploading(false);
      return;
    }
    if (!conversationText.trim() && selectedFiles.length === 0) {
      setError('Please paste conversation text or upload at least one screenshot');
      setUploading(false);
      return;
    }
    if (selectedFiles.length > 0) {
      const validationErrors = validateFiles(selectedFiles);
      if (validationErrors.length > 0) {
        setError(validationErrors[0].message);
        setUploading(false);
        return;
      }
    }
    // GA4: safe, non-sensitive scan metadata only (never the conversation itself)
    const ga4InputType: ScanInputType = conversationText.trim().length > 0 && selectedFiles.length > 0 ? 'text_and_screenshot' : selectedFiles.length > 0 ? 'screenshot' : 'text';
    trackScanStarted(selectedMode, ga4InputType);
    // First-party: fires only once validation and credit checks have passed.
    void trackEvent('scan_started', {
      mode: selectedMode,
      input_type: ga4InputType,
      file_count: selectedFiles.length
    });
    // Track scan initiation
    await trackCriticalEvent('Scan_Initiated', {
      conversation_name: conversationName,
      has_text_input: conversationText.trim().length > 0,
      has_file_uploads: selectedFiles.length > 0,
      file_count: selectedFiles.length,
      file_types: selectedFiles.map(f => f.type),
      analysis_mode: selectedMode,
      is_pro: isPro,
      scans_remaining_before: scansCount
    });
    setAnalysisStatus('pending');
    let scanId: string | null = null;
    let realtimeChannel: any = null;
    let timeoutId: NodeJS.Timeout | null = null;
    // Helper function to cleanup and exit processing state
    const exitProcessingState = async (status: 'completed' | 'failed' | 'idle') => {
      console.log('[EXIT PROCESSING] Setting status to:', status);
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
        realtimeChannel = null;
      }
      setAnalysisStatus(status);
      setUploading(false);
      // CRITICAL: Always re-fetch credits from backend after any scan event
      console.log('[EXIT PROCESSING] Re-fetching credits from backend...');
      await refetchCredits();
      console.log('[EXIT PROCESSING] Credits re-fetched');
    };
    try {
      // Upload files first if any
      let fileUrls: string[] = [];
      if (selectedFiles.length > 0) {
        const progress: UploadProgress[] = selectedFiles.map(file => ({
          fileName: file.name,
          progress: 0,
          status: 'pending'
        }));
        setUploadProgress(progress);
        fileUrls = await uploadFiles(selectedFiles, user.id, (fileName, prog) => {
          setUploadProgress(prev => prev.map(p => p.fileName === fileName ? {
            ...p,
            progress: prog,
            status: 'completed'
          } : p));
        });
        // Track upload completion
        const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024);
        await trackCriticalEvent('Scan_Upload_Completed', {
          scan_id: scanId || 'pending',
          file_count: selectedFiles.length,
          total_file_size_mb: Math.round(totalSize * 100) / 100,
          upload_duration_seconds: 0
        });
      }
      const conversationTextValue = conversationText.trim() || '';
      console.log('[SCAN INSERT] Preparing scan data:', {
        user_id: user.id,
        title: conversationName,
        mode: selectedMode,
        has_files: fileUrls.length > 0,
        has_text: conversationTextValue.length > 0,
        text_length: conversationTextValue.length
      });
      // Insert scan record. The backend owns status, score, risk_level,
      // analysis and is_unlocked (DB default false) — never send them.
      // title is required by the backend, so it is always sent.
      const scanPayload: {
        user_id: string;
        title: string;
        mode: string;
        conversation_text?: string;
        file_urls?: string[];
      } = {
        user_id: user.id,
        title: conversationName?.trim() || 'Untitled conversation',
        mode: selectedMode
      };
      if (conversationTextValue) scanPayload.conversation_text = conversationTextValue;
      if (fileUrls.length > 0) scanPayload.file_urls = fileUrls;
      const {
        data: scanData,
        error: insertError
      } = await supabase.from('scans').insert(scanPayload).select().single();
      if (insertError) {
        console.error('[SCAN INSERT] Error:', insertError);
        throw insertError;
      }
      scanId = scanData.id;
      setCurrentScanId(scanId);
      console.log('[SCAN INSERT] Success - Scan ID:', scanId);
      // Set up timeout - 30 seconds for initial response
      timeoutId = setTimeout(() => {
        console.log('[TIMEOUT] No status update after 30s - scan processing in background');
        setAnalysisStatus('processing');
        showToast("Analysis is taking longer than expected. You'll be notified when complete.", 'info');
      }, 30000);
      // Set up realtime subscription
      realtimeChannel = supabase.channel(`scan-${scanId}`).on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'scans',
        filter: `id=eq.${scanId}`
      }, async payload => {
        const updatedScan = payload.new as any;
        console.log('[REALTIME] Scan update received:', {
          status: updatedScan.status,
          score: updatedScan.score,
          risk_level: updatedScan.risk_level,
          has_analysis: !!updatedScan.analysis
        });
        // Clear timeout when we receive any status update
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (updatedScan.status === 'processing') {
          setAnalysisStatus('processing');
        }
        if (updatedScan.status === 'completed') {
          // CRITICAL: Distinguish between a real score of 0 and a missing/null score.
          // A null score means the backend never computed one (OCR failed, empty input, etc.)
          const hasRealScore = typeof updatedScan.score === 'number' && !Number.isNaN(updatedScan.score);
          const hasRealRisk = typeof updatedScan.risk_level === 'string' && updatedScan.risk_level.length > 0;
          if (!hasRealScore && !hasRealRisk) {
            console.warn('[REALTIME] Scan marked completed but score & risk_level are null - backend extracted no content.');
            setScanResult({
              errorMessage: "We couldn't extract a conversation from your upload. Please try with clearer screenshots or paste the conversation text directly."
            });
            setShowResultModal(true);
            trackScanFailed(selectedMode, ga4InputType, 'no_content_found');
            await exitProcessingState('failed');
            return;
          }
          setScanResult({
            score: hasRealScore ? updatedScan.score : undefined,
            riskLevel: hasRealRisk ? updatedScan.risk_level : undefined
          });
          setShowResultModal(true);
          setShowNotification(true);
          // Track analysis completion
          trackCriticalEvent('Scan_Analysis_Completed', {
            scan_id: scanId,
            toxicity_score: updatedScan.score || 0,
            risk_level: updatedScan.risk_level || 'unknown',
            patterns_detected: Array.isArray(updatedScan.patterns) ? updatedScan.patterns.length : 0,
            message_count: updatedScan.message_count || 0,
            analysis_duration_seconds: 0
          });
          trackScanCompleted(selectedMode, ga4InputType, hasRealRisk ? updatedScan.risk_level : undefined, hasRealScore ? updatedScan.score : undefined);
          // First-party: keyed on the real scan id so repeated realtime
          // payloads for the same scan cannot double-count.
          void trackEventOnce(`scan_completed:${scanId}`, 'scan_completed', {
            scan_id: scanId,
            mode: selectedMode,
            score: hasRealScore ? updatedScan.score : null,
            risk_level: hasRealRisk ? updatedScan.risk_level : null
          });
          // CRITICAL: Exit processing state and re-fetch credits
          await exitProcessingState('completed');
        }
        if (updatedScan.status === 'failed') {
          const errorMsg = 'Analysis failed. Please try again.';
          setScanResult({
            errorMessage: errorMsg
          });
          setError(errorMsg);
          setShowResultModal(true);
          // Track analysis failure
          trackCriticalEvent('Scan_Analysis_Failed', {
            scan_id: scanId,
            error_type: 'analysis_failed',
            retry_attempted: false
          });
          trackScanFailed(selectedMode, ga4InputType, 'analysis_failed');
          // CRITICAL: Exit processing state and re-fetch credits
          await exitProcessingState('failed');
        }
      }).subscribe();
      // Trigger async analysis
      console.log('[ANALYSIS] Triggering async analysis...');
      try {
        const analysisResponse = await triggerAsyncAnalysis(scanId, fileUrls, user.id, conversationTextValue || undefined, selectedMode);
        // CRITICAL: Check if backend returned "not allowed" response
        if (analysisResponse && analysisResponse.allowed === false) {
          console.log('[ANALYSIS] Backend blocked scan:', analysisResponse.reason);
          if (analysisResponse.reason === 'NO_CREDITS') {
            // Show paywall modal
            await trackCriticalEvent('Paywall_Displayed', {
              trigger_action: 'scan_response',
              is_pro: isPro,
              scans_remaining: 0,
              reason: 'backend_response_no_credits'
            });
            setScanResult({
              errorMessage: 'No scans remaining. Please purchase a subscription to continue.'
            });
            setShowPaywallModal(true);
            setShowResultModal(true);
            await exitProcessingState('failed');
            return;
          }
          // Generic not allowed
          setScanResult({
            errorMessage: analysisResponse.reason || 'Scan not allowed'
          });
          setShowResultModal(true);
          await exitProcessingState('failed');
          return;
        }
        console.log('[ANALYSIS] Trigger successful');
        // Track analysis start
        await trackCriticalEvent('Scan_Analysis_Started', {
          scan_id: scanId,
          analysis_mode: selectedMode,
          has_files: fileUrls.length > 0,
          has_text: conversationTextValue.length > 0
        });
      } catch (analysisError: any) {
        console.error('[ANALYSIS] Edge Function error:', analysisError);
        // Clear timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        // Check if it's a server overload error
        if (analysisError.message === 'SERVER_OVERLOAD') {
          console.log('[ANALYSIS] Server overload - scan will process automatically');
          showToast('Server is experiencing high load. Your scan is queued and will process automatically. Check back in a few minutes.', 'info');
          setAnalysisStatus('processing');
          // CRITICAL: Re-fetch credits even on server overload
          await refetchCredits();
          setTimeout(() => {
            setUploading(false);
            navigate('/dashboard');
          }, 3000);
          return;
        }
        // Scan status is owned by the backend — the client never updates scans.
        // Track failure
        await trackCriticalEvent('Scan_Analysis_Failed', {
          scan_id: scanId,
          error_type: analysisError.message || 'unknown_error',
          retry_attempted: false
        });
        let errorMessage = 'Failed to start analysis. Please try again.';
        if (analysisError.message?.includes('520') || analysisError.message?.includes('502')) {
          errorMessage = 'Server temporarily unavailable. Your scan is saved and will process automatically.';
        } else if (analysisError.message) {
          errorMessage = analysisError.message;
        }
        setScanResult({
          errorMessage: errorMessage
        });
        setError(errorMessage);
        setShowResultModal(true);
        // CRITICAL: Exit processing state
        await exitProcessingState('failed');
      }
    } catch (err: any) {
      console.error('[SUBMIT] Error in handleSubmit:', err);
      let errorMessage = 'Failed to analyze conversation. Please try again.';
      if (err.message?.includes('520') || err.message?.includes('502')) {
        errorMessage = 'Server temporarily unavailable. Please try again in a few moments.';
      } else if (err.code === 'PGRST116') {
        errorMessage = 'Database error. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setScanResult({
        errorMessage: errorMessage
      });
      setUploadProgress([]);
      setShowResultModal(true);
      // CRITICAL: Exit processing state
      await exitProcessingState('failed');
    }
  };
  useEffect(() => {
    return () => {
      const channels = supabase.getChannels();
      channels.forEach(channel => {
        if (channel.topic.startsWith('scan-')) {
          supabase.removeChannel(channel);
        }
      });
    };
  }, []);
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-10223" data-mp="1pqghen5cc6wv">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-10224" data-mp="58fis2m4fvth" />

      <WebsiteHeader data-id="element-10225" data-mp="9cmb6k7ixx4" />

      {/* Processing Overlay */}
      {uploading && <ForensicTerminalOverlay status={analysisStatus} mode={selectedMode} onTimeout={() => {
      console.log('[OVERLAY] Timeout triggered');
    }} data-id="element-10226" data-mp="o1jerumtakr9" />}

      {/* Result Modal */}
      <ScanResultModal isOpen={showResultModal} status={analysisStatus === 'completed' ? 'completed' : 'failed'} score={scanResult.score} riskLevel={scanResult.riskLevel} errorMessage={scanResult.errorMessage} onViewResults={() => navigate(`/analysis?id=${currentScanId}`)} onRetry={() => {
      setShowResultModal(false);
      setUploading(false);
      setAnalysisStatus('idle');
    }} onClose={() => {
      setShowResultModal(false);
      navigate('/dashboard');
    }} data-id="element-10227" data-mp="jzemp0c0hw6q" />

      {showNotification && <NotificationBanner message="Analysis Complete. Report Generated." onClose={() => setShowNotification(false)} data-id="element-10228" data-mp="wi0l9kyv3e1j" />}

      {/* Paywall Modal */}
      <AnimatePresence data-id="element-10229" data-mp="rgkxhl5autfg">
        {showPaywallModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPaywallModal(false)} data-id="element-10230" data-mp="mj2x4j5clmj2">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border-2 border-[#FF0000] max-w-3xl w-full max-h-[92vh] overflow-y-auto p-8 relative" data-id="element-10231" data-mp="27y8nfladsnco">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-10232" data-mp="36wzqkqw2xj3i" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-10233" data-mp="3bw2o7q9qcn1g" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-10234" data-mp="3fvuxy6av0z8e" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-10235" data-mp="3kwiq4vatrm47" />

              <button onClick={() => setShowPaywallModal(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors" data-id="element-10236" data-mp="v5vqdxxq8s6b">
                <X size={20} data-id="element-10237" data-mp="btg44vq3h5r7" />
              </button>

              <div className="text-center mb-6" data-id="element-10238" data-mp="6udcqe31ey1p">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 border border-[#FF0000] mb-4" data-id="element-10239" data-mp="6vogidwhg5st">
                  <Lock className="w-8 h-8 text-[#FF0000]" data-id="element-10240" data-mp="fujljmnsj9uj" />
                </div>
                <h3 className="text-2xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-10241" data-mp="fsf8of7wz0i4">
                  {paywallReason === 'mode' ? 'Unlock Romantic & Ex-Partner Modes' : 'No Scans Remaining'}
                </h3>
                <p className="text-gray-400 font-mono text-sm" data-id="element-10242" data-mp="13j2qptbjn6ie">
                  {paywallReason === 'mode' ? 'Your free scan covers Standard, Friendship, Family and Workplace. Upgrade for deep romantic and ex-partner analysis.' : 'Buy a single full forensic scan, or go unlimited with a subscription.'}
                </p>
              </div>

              {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-mono" data-id="element-10243" data-mp="2r6gz55f8u8dm">
                  {error}
                </div>}

              <PlanOptionCards plans={paidPlans} onCheckout={handleCheckout} pendingPlan={pendingPlan} currentPlanId={planId} data-id="element-10244" data-mp="2hf2s8ckyq6ag" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-lg mx-auto w-full relative z-10" data-id="element-10245" data-mp="2chkiwl319p1x">
        <motion.main className="px-6 pt-12 pb-24" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10246" data-mp="2ceoi8ozswm9s">
          {/* Page Header */}
          <motion.div variants={itemVariants} className="text-center mb-10" data-id="element-10247" data-mp="23igz1ew1l5md">
            <div className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6 border border-[#FF0000]/30 shadow-[0_0_15px_rgba(255,0,0,0.2)]" data-id="element-10248" data-mp="3svu456bzaxoj">
              <FileUp size={14} data-id="element-10249" data-mp="13ejdhxeslml5" />
              <span data-id="element-10250" data-mp="18fh2asub5evm">Evidence Upload</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-4 uppercase tracking-tight" data-id="element-10251" data-mp="18g0f5v53pd2s">
              Upload Chat Data
            </h1>
            <p className="text-gray-400 leading-relaxed text-sm max-w-sm mx-auto font-mono" data-id="element-10252" data-mp="1i61xe6arw19s">
              Paste conversation text or upload screenshots to identify toxicity
              patterns and hidden manipulation.
            </p>
          </motion.div>

          {/* Plan status + contextual upgrade panel */}
          {!loadingCredits && !loadingPlan && <motion.div variants={itemVariants} className="bg-[#0a0a0a] p-6 mb-8 border border-gray-800 relative overflow-hidden group" data-id="element-10253" data-mp="1w3grhobz0x83">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-10254" data-mp="1t4wjqjelkdj" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-10255" data-mp="6qn4bdloptjy" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-10256" data-mp="2k1y1ey5gjd5x" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-10257" data-mp="2f8n2rkasu2p3" />

              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF0000]/5 rounded-full blur-3xl -mr-16 -mt-16 transition-opacity opacity-50 group-hover:opacity-100" data-id="element-10258" data-mp="2b8l1vdcw1dav"></div>

              <div className="flex items-start gap-4 relative z-10" data-id="element-10259" data-mp="32y7ogayc7e22">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-900 flex items-center justify-center border border-gray-800" data-id="element-10260" data-mp="2yxe0xiot419p">
                  <Shield size={24} className="text-gray-500" data-id="element-10261" data-mp="2txj6541wurjd" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-10262" data-mp="2oyoerdpvnunn">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-10263" data-mp="2p23v0jvaf9qn">
                    Your plan
                  </p>
                  <h3 className="mt-1 text-xl font-black text-white uppercase tracking-tight" data-id="element-10264" data-mp="1djt2fyepe5a9">
                    {planLabel}
                  </h3>
                  <p className="mt-1 text-sm text-gray-400 leading-relaxed font-mono" data-id="element-10265" data-mp="1k0uir4vrtf4s">
                    {accessLabel}
                  </p>
                  {creditsUnknown && <p className="mt-1 font-mono text-xs text-[#FFD700]" data-id="element-10266" data-mp="16361s84tqs50">
                      {refetchError ? 'Couldn’t read your credits just now — we’ll re-check when you start a scan.' : 'Checking your credits…'}{' '}
                      <button type="button" onClick={() => void refetchCredits()} className="underline transition-colors duration-150 ease-out hover:text-white" data-id="element-10267" data-mp="113jl1opsetrg">
                        Retry now
                      </button>
                    </p>}
                </div>
              </div>

              {!unlimited && <div className="relative z-10 mt-6 border-t border-gray-800 pt-5" data-id="element-10268" data-mp="3uv39uir0yl7f">
                  <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-10269" data-mp="3ux5yozgfy8ak">
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-gray-400" data-id="element-10270" data-mp="3pxtc8fthf38i">
                      Need more analysis?
                    </p>
                    {canScan && <button type="button" onClick={() => setShowUpgradePanel(open => !open)} aria-expanded={showUpgradePanel} className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF0000] transition-colors duration-150 ease-out hover:text-white" data-id="element-10271" data-mp="3qv3vl20y3ro">
                        {showUpgradePanel ? 'Hide plans' : 'View plans'}
                      </button>}
                  </div>

                  <AnimatePresence initial={false} data-id="element-10272" data-mp="we8rutnqvozn">
                    {(showUpgradePanel || !canScan) && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-10273" data-mp="2pv52j6wow4e9">
                        <div className="pt-4" data-id="element-10274" data-mp="2kxldrd2d8982">
                          <PlanOptionCards plans={paidPlans} onCheckout={handleCheckout} pendingPlan={pendingPlan} currentPlanId={planId} data-id="element-10275" data-mp="2kyen8lsplctw" />
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>}
            </motion.div>}

          {/* Error Message */}
          <AnimatePresence data-id="element-10276" data-mp="1buqomcqbe0c0">
            {error && <motion.div initial={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} animate={{
            opacity: 1,
            height: 'auto',
            marginBottom: 24
          }} exit={{
            opacity: 0,
            height: 0,
            marginBottom: 0
          }} className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 flex items-start gap-3 overflow-hidden" data-id="element-10277" data-mp="16ya2tp6l7y23">
                <AlertCircle className="text-[#FF0000] flex-shrink-0 mt-0.5" size={20} data-id="element-10278" data-mp="2icx4lj942u7g" />
                <p className="text-[#FF0000] text-sm font-mono" data-id="element-10279" data-mp="31gh3xhpo6sqa">{error}</p>
              </motion.div>}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-10280" data-mp="2sgkjxte86i1k">
            {/* Mode Selector */}
            <motion.div variants={itemVariants} data-id="element-10281" data-mp="2sere2gbfs1um">
              <ModeSelector selectedMode={selectedMode} onModeChange={setSelectedMode} disabled={uploading} data-id="element-10282" data-mp="2xggopuuqydi6" />
            </motion.div>

            {/* Conversation Name */}
            <motion.div variants={itemVariants} className="bg-[#0a0a0a] p-6 border border-gray-800 relative" data-id="element-10283" data-mp="3p4qsusi6jwln">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-10284" data-mp="3g5dh1t6l2vxr" />
              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-10285" data-mp="1zrnbehztfzm0" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-10286" data-mp="24r9p0sjk3hvc" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-10287" data-mp="1x6pdt5160lht" />

              <label className="block text-xs font-bold text-gray-500 mb-3 uppercase tracking-widest font-mono" data-id="element-10288" data-mp="1s7cg1p8r9u0g">
                Case Title / Subject
              </label>
              <input type="text" placeholder="e.g., Chat with Alex" value={conversationName} onChange={e => setConversationName(e.target.value)} disabled={uploading} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white placeholder-gray-600 transition-all text-sm font-mono" data-id="element-10289" data-mp="219bymlzf565i" />
            </motion.div>

            {/* Upload Method Tabs */}
            <motion.div variants={itemVariants} className="bg-[#0a0a0a] border border-gray-800 relative" data-id="element-10290" data-mp="urffnn033jp9">
              <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-10291" data-mp="58d5ldolq5qo" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-10292" data-mp="99f8q5ln31xx" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-10293" data-mp="e91lkdxk2g67" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-10294" data-mp="j7l9gjvpm217" />

              {/* Text Input Section */}
              <div className="p-6 border-b border-gray-800" data-id="element-10295" data-mp="o66bmouiuvsa">
                <div className="flex items-center gap-2 mb-3" data-id="element-10296" data-mp="s8ayn4qgsu5y">
                  <div className="p-1.5 bg-gray-900 border border-gray-800" data-id="element-10297" data-mp="s6gfnvyr7c4i">
                    <FileText size={14} className="text-gray-400" data-id="element-10298" data-mp="wzt3w4zitgwq" />
                  </div>
                  <label className="text-sm font-bold text-white uppercase tracking-wide" data-id="element-10299" data-mp="11x1cbduva6ks">
                    Paste Text Data
                  </label>
                  <span className="text-[10px] text-gray-500 font-mono bg-gray-900 px-2 py-0.5 border border-gray-800 uppercase" data-id="element-10300" data-mp="1bwjvvt6v7qcb">
                    Optional
                  </span>
                </div>
                <div className="relative" data-id="element-10301" data-mp="2rfrfggv5g2df">
                  {uploading && <div className="scanning-line" data-id="element-10302" data-mp="2re6kwr8u4jfk"></div>}
                  <textarea placeholder="Paste raw conversation logs here..." value={conversationText} onChange={e => setConversationText(e.target.value)} disabled={uploading} rows={6} className="w-full px-4 py-3 bg-[#050505] border border-gray-800 focus:outline-none focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white placeholder-gray-600 resize-none transition-all text-sm font-mono leading-relaxed" data-id="element-10303" data-mp="2mflkortl1zr4" />
                </div>
              </div>

              {/* File Upload Section */}
              <div className="p-6" data-id="element-10304" data-mp="1rh3weeekf11">
                <div className="flex items-center gap-2 mb-3" data-id="element-10305" data-mp="3t53mxufg3yey">
                  <div className="p-1.5 bg-gray-900 border border-gray-800" data-id="element-10306" data-mp="3t3iohtq0fvbt">
                    <ImageIcon size={14} className="text-gray-400" data-id="element-10307" data-mp="1sc60fokqyhlh" />
                  </div>
                  <label className="text-sm font-bold text-white uppercase tracking-wide" data-id="element-10308" data-mp="1nfpacotzlt5r">
                    Upload Evidence
                  </label>
                  <span className="text-[10px] text-gray-500 font-mono bg-gray-900 px-2 py-0.5 border border-gray-800 uppercase" data-id="element-10309" data-mp="1doku7lsks29c">
                    Optional
                  </span>
                </div>

                <motion.div whileHover={!uploading ? {
                backgroundColor: 'rgba(255, 0, 0, 0.05)',
                borderColor: '#FF0000'
              } : {}} className="border-2 border-dashed border-gray-800 p-8 text-center transition-all bg-[#050505] group" data-id="element-10310" data-mp="14o684k7sej4s">
                  <input type="file" id="upload-files" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" multiple disabled={uploading} data-id="element-10311" data-mp="2p51eptizl932" />
                  <label htmlFor="upload-files" className={`cursor-pointer block ${uploading ? 'cursor-not-allowed opacity-50' : ''}`} data-id="element-10312" data-mp="36du8iwuut9p3">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0a0a0a] mb-4 border border-gray-800 group-hover:border-[#FF0000] transition-colors" data-id="element-10313" data-mp="2enh66rw46r5t">
                      <UploadIcon className="w-6 h-6 text-gray-400 group-hover:text-[#FF0000] transition-colors" data-id="element-10314" data-mp="2nlj8rbvhxrv7" />
                    </div>
                    <p className="font-bold text-white mb-1 uppercase tracking-wide text-sm" data-id="element-10315" data-mp="2sjkw48zygdch">
                      Drop Screenshots Here
                    </p>
                    <p className="text-[10px] text-gray-500 font-mono uppercase" data-id="element-10316" data-mp="7u579jkaqyph">
                      Max 10 files • 5MB each • JPG/PNG/PDF
                    </p>
                  </label>
                </motion.div>

                {/* Selected Files */}
                <AnimatePresence data-id="element-10317" data-mp="3oal5c977bpq7">
                  {selectedFiles.length > 0 && !uploading && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="mt-4 space-y-2" data-id="element-10318" data-mp="3jc1i85gvg7w9">
                      {selectedFiles.map((file, index) => <motion.div key={`${file.name}-${index}`} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: 10
                  }} className="flex items-center justify-between bg-[#050505] p-3 border border-gray-800 group" data-id="element-10319" data-mp="2cu4u6s2ojv95" data-mpi={index}>
                          <div className="flex items-center gap-3 overflow-hidden" data-id="element-10320" data-mp="1b4ojdakayz2p">
                            <div className="w-8 h-8 bg-gray-900 flex items-center justify-center border border-gray-800 flex-shrink-0" data-id="element-10321" data-mp="x4nivw7y9tnr">
                              <ImageIcon size={14} className="text-gray-500" data-id="element-10322" data-mp="114nwxfr502ua" />
                            </div>
                            <span className="text-sm text-gray-300 truncate font-mono" data-id="element-10323" data-mp="3ht1ind9rcla1">
                              {file.name}
                            </span>
                          </div>
                          <button type="button" onClick={() => removeFile(index)} className="p-1.5 hover:bg-[#FF0000]/10 hover:text-[#FF0000] text-gray-500 transition-colors" aria-label="Remove file" data-id="element-10324" data-mp="5zj970u0018v">
                            <X size={14} data-id="element-10325" data-mp="owst0j38jgqn" />
                          </button>
                        </motion.div>)}
                    </motion.div>}
                </AnimatePresence>

                {/* Upload Progress */}
                {uploading && uploadProgress.length > 0 && <div className="mt-4 space-y-2" data-id="element-10326" data-mp="axkulsewccni">
                    {uploadProgress.map((progress, index) => <div key={index} className="flex items-center bg-[#050505] p-3 border border-gray-800" data-id="element-10327" data-mp="2zpwzc0spn9ar" data-mpi={index}>
                        <span className="text-sm text-gray-300 truncate flex-1 font-mono" data-id="element-10328" data-mp="1b5rgt98uhfxx">
                          {progress.fileName}
                        </span>
                        {progress.status === 'completed' ? <CheckCircle size={16} className="text-green-500 ml-3" data-id="element-10329" data-mp="1k4cyl26hsqxn" /> : <div className="w-4 h-4 border-2 border-gray-800 border-t-[#FF0000] rounded-full animate-spin ml-3" data-id="element-10330" data-mp="1tuxu2gqvdfcc"></div>}
                      </div>)}
                  </div>}
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.button variants={itemVariants} whileHover={!uploading && !blockedByCredits && (conversationText.trim() || selectedFiles.length > 0) ? {
            scale: 1.02,
            boxShadow: '0 0 20px rgba(255, 0, 0, 0.4)'
          } : {}} whileTap={!uploading && !blockedByCredits ? {
            scale: 0.98
          } : {}} type="submit" disabled={uploading || !conversationText.trim() && selectedFiles.length === 0 || loadingCredits || blockedByCredits} className="w-full bg-[#FF0000] text-white py-4 font-bold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(255,0,0,0.2)] flex items-center justify-center gap-2 uppercase tracking-widest text-sm" data-id="element-10331" data-mp="27teax0ig4m87">
              {uploading ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10332" data-mp="2djys0a5lp5hf"></div>
                  <span data-id="element-10333" data-mp="2x57emfq6n0hr">Running Analysis...</span>
                </> : loadingCredits ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-10334" data-mp="32167afijn4nz"></div>
                  <span data-id="element-10335" data-mp="2s52ywrx3m9sl">Verifying credits...</span>
                </> : blockedByCredits ? <>
                  <Lock size={18} data-id="element-10336" data-mp="2ecws3wfu5f5t" />
                  <span data-id="element-10337" data-mp="2ebv46rdq0y6o">No scans remaining. Purchase to continue.</span>
                </> : <>
                  <Zap size={18} className="fill-white" data-id="element-10338" data-mp="24g0jlqpxst19" />
                  <span data-id="element-10339" data-mp="24hldde4fg7y2">Initiate Scan</span>
                </>}
            </motion.button>
          </form>

          {/* Tips Section */}
          <motion.div variants={itemVariants} className="bg-[#0a0a0a] border border-gray-800 p-6 mt-10 relative" data-id="element-10340" data-mp="1vek8thv7mowi">
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-10341" data-mp="1zfvxnl69f63n" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-10342" data-mp="2fvexb5gkadt3" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-10343" data-mp="2axv85aibsgmd" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-10344" data-mp="25wfp1nzpkk6m" />

            <h3 className="font-bold text-white mb-4 flex items-center gap-2 text-xs uppercase tracking-widest font-mono" data-id="element-10345" data-mp="20ynvkpjf0q0b">
              <Info size={14} className="text-[#FF0000]" data-id="element-10346" data-mp="1wz5d00f2gr0n" />
              <span data-id="element-10347" data-mp="1s5u29o74vq3g">Protocol Guidelines</span>
            </h3>
            <ul className="space-y-3 text-xs text-gray-400 font-mono" data-id="element-10348" data-mp="1n79928qau6oi">
              <li className="flex items-start gap-3" data-id="element-10349" data-mp="1n56cf5un5fat">
                <div className="w-1 h-1 bg-[#FF0000] mt-1.5 flex-shrink-0" data-id="element-10350" data-mp="1i9h8kqbl9c8p"></div>
                <span data-id="element-10351" data-mp="1d9uygpr24c44">
                  Include multiple messages for accurate pattern triangulation
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-10352" data-mp="198jdwyn1ih2s">
                <div className="w-1 h-1 bg-[#FF0000] mt-1.5 flex-shrink-0" data-id="element-10353" data-mp="3kvmoro99g7k1"></div>
                <span data-id="element-10354" data-mp="3owevoaunu0d5">Raw text logs and screenshots are equally effective</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-10355" data-mp="3tw18ij6ktelf">
                <div className="w-1 h-1 bg-[#FF0000] mt-1.5 flex-shrink-0" data-id="element-10356" data-mp="3twaza8zg3ps6"></div>
                <span data-id="element-10357" data-mp="2of7kyb8rq89">
                  <Lock size={10} className="inline mr-1" data-id="element-10358" data-mp="2op1uy3bkjjr" />
                  Data is encrypted end-to-end. Zero-knowledge architecture.
                </span>
              </li>
            </ul>
          </motion.div>
        </motion.main>
      </div>
    </div>;
}